import React, { useEffect, useRef, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, Loader2, Download, Star } from "lucide-react";
import { API } from "../App";
import { buildReviewUrl } from "../lib/reviewUrl";

/**
 * Landing page after Revolut redirects on successful balance payment.
 * URL: /balance-success?booking_id=X
 *
 * We poll the balance-summary endpoint until balance_paid=true. Once true,
 * we fetch the invoice details and offer a PDF download + review CTA.
 */
const POLL_INTERVAL_MS = 2500;
const MAX_POLL_TIME_MS = 30_000;

const BalanceSuccessPage = () => {
  const [params] = useSearchParams();
  const bookingId = params.get("booking_id") || "";
  const [status, setStatus] = useState("checking");
  const [invoice, setInvoice] = useState(null);
  const startedRef = useRef(Date.now());

  useEffect(() => {
    if (!bookingId) {
      setStatus("error");
      return;
    }
    let cancelled = false;
    let timeoutId;

    const poll = async () => {
      if (cancelled) return;
      try {
        const r = await axios.get(`${API}/payments/balance-summary/${bookingId}`);
        if (cancelled) return;
        if (r.data?.balance_paid) {
          if (r.data.invoice_id) {
            try {
              const inv = await axios.get(`${API}/invoices/${r.data.invoice_id}`);
              setInvoice(inv.data);
            } catch {
              // fallthrough — invoice generation might race, poll again shortly
              if (Date.now() - startedRef.current < MAX_POLL_TIME_MS) {
                timeoutId = setTimeout(poll, POLL_INTERVAL_MS);
                return;
              }
            }
          }
          setStatus("paid");
          return;
        }
        if (Date.now() - startedRef.current < MAX_POLL_TIME_MS) {
          timeoutId = setTimeout(poll, POLL_INTERVAL_MS);
        } else {
          setStatus("pending");
        }
      } catch {
        if (Date.now() - startedRef.current < MAX_POLL_TIME_MS) {
          timeoutId = setTimeout(poll, POLL_INTERVAL_MS);
        } else {
          setStatus("error");
        }
      }
    };
    poll();
    return () => {
      cancelled = true;
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [bookingId]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 flex items-center justify-center p-4">
      <div
        className="max-w-lg w-full bg-slate-900/85 border border-slate-700 rounded-2xl shadow-2xl p-8 text-center"
        data-testid="balance-success-page"
      >
        {status === "checking" && (
          <>
            <Loader2 className="w-16 h-16 text-saturn-orange mx-auto mb-4 animate-spin" />
            <h1 className="text-2xl font-bold text-white mb-2">Confirming your payment…</h1>
            <p className="text-slate-400">Generating your invoice — just a moment.</p>
          </>
        )}

        {status === "paid" && (
          <>
            <div className="w-20 h-20 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="w-12 h-12 text-green-400" />
            </div>
            <h1 className="text-3xl font-bold text-white mb-2">Paid in full ✅</h1>
            <p className="text-slate-300 mb-1">Your VAT invoice has just been emailed to you.</p>
            <p className="text-slate-500 text-sm mb-6">
              Booking <span className="font-mono">{bookingId}</span>
              {invoice && (
                <> · Invoice <span className="font-mono text-saturn-orange">{invoice.invoice_number}</span></>
              )}
            </p>

            {invoice && (
              <a
                href={`${API}/invoices/${invoice.invoice_id}/pdf`}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 bg-saturn-orange hover:bg-saturn-orange/80 text-white px-6 py-3 rounded-lg font-medium transition mb-3"
                data-testid="download-invoice-btn"
              >
                <Download className="w-4 h-4" />
                Download invoice PDF
              </a>
            )}

            <div className="bg-slate-800/60 border border-slate-700 rounded-lg p-4 my-6">
              <p className="text-white font-medium mb-2 flex items-center justify-center gap-2">
                <Star className="w-4 h-4 text-yellow-400" />
                Enjoying the finish?
              </p>
              <p className="text-slate-400 text-sm mb-3">A quick review means the world to us.</p>
              <a
                href={buildReviewUrl(bookingId, "balance_success")}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block bg-amber-500 hover:bg-amber-600 text-slate-900 px-5 py-2 rounded-lg font-bold text-sm"
                data-testid="review-cta-btn"
              >
                ⭐ Leave a review
              </a>
            </div>

            <Link to="/" className="text-saturn-orange text-sm underline">
              Back to homepage
            </Link>
          </>
        )}

        {status === "pending" && (
          <>
            <Loader2 className="w-16 h-16 text-yellow-400 mx-auto mb-4 animate-spin" />
            <h1 className="text-2xl font-bold text-white mb-2">Payment is processing</h1>
            <p className="text-slate-400">You'll receive the invoice by email as soon as it clears. No need to pay again.</p>
          </>
        )}

        {status === "error" && (
          <>
            <h1 className="text-2xl font-bold text-white mb-2">We couldn't verify that booking</h1>
            <p className="text-slate-400 mb-4">If you were charged, contact us via WhatsApp and we'll sort it.</p>
            <Link to="/" className="text-saturn-orange underline">Go to homepage</Link>
          </>
        )}
      </div>
    </div>
  );
};

export default BalanceSuccessPage;
